<template>
  <div v-if="isDashboardAdmin">
    <b-button
      type="button"
      variant="outline-primary"
      :disabled="!canSearch"
      @click="open"
    >
      <feather-icon
        icon="SearchIcon"
        class="mr-50"
      />
      {{ $t('site_domain_picker.open') }}
    </b-button>
    <small
      v-if="!canSearch"
      class="d-block text-muted mt-50"
    >
      {{ $t(widgetId ? 'site_domain_picker.paths_required' : 'site_domain_picker.save_first') }}
    </small>

    <b-modal
      v-model="visible"
      :title="$t('site_domain_picker.title')"
      :aria-label="$t('site_domain_picker.title')"
      :cancel-title="$t('site_domain_picker.close')"
      :ok-title="$t('site_domain_picker.close')"
      :header-close-label="$t('site_domain_picker.close')"
      ok-only
      scrollable
      @hide="invalidate"
    >
      <p class="text-muted">
        {{ $t('site_domain_picker.saved_settings') }}
      </p>
      <b-form @submit.prevent="search">
        <b-form-group
          :label="$t('site_domain_picker.filter')"
          :label-for="`${_uid}-domain-filter`"
        >
          <b-form-input
            :id="`${_uid}-domain-filter`"
            v-model="filter"
            autofocus
            autocomplete="off"
            placeholder="example.com"
            :disabled="loading"
          />
          <small class="d-block text-muted mt-50">{{ $t('site_domain_picker.filter_help') }}</small>
        </b-form-group>
        <b-button
          type="submit"
          variant="primary"
          :disabled="loading || !canSearch"
        >
          {{ $t('site_domain_picker.search') }}
        </b-button>
      </b-form>

      <div
        class="mt-2"
        aria-live="polite"
        :aria-busy="loading ? 'true' : 'false'"
      >
        <div
          v-if="loading"
          role="status"
        >
          <b-spinner
            small
            class="mr-50"
          />
          {{ $t('site_domain_picker.loading') }}
        </div>
        <b-alert
          v-else-if="error"
          show
          variant="danger"
        >
          {{ error }}
        </b-alert>
        <p
          v-else-if="searched && !domains.length"
          class="mb-0"
        >
          {{ $t('site_domain_picker.empty') }}
        </p>
        <ul
          v-else-if="domains.length"
          class="list-unstyled mb-0"
        >
          <li
            v-for="domain in domains"
            :key="domain"
            class="d-flex align-items-center border-bottom py-1"
          >
            <span class="text-break flex-grow-1 mr-1">{{ domain }}</span>
            <b-button
              type="button"
              size="sm"
              variant="outline-primary"
              :aria-label="$t('site_domain_picker.select_domain', { domain })"
              @click="select(domain)"
            >
              {{ $t('site_domain_picker.select') }}
            </b-button>
          </li>
        </ul>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert, BButton, BForm, BFormGroup, BFormInput, BModal, BSpinner,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import { getUserData } from '@/auth/utils'
import { isDashboardAdmin } from '@/auth/roles'
import parseSiteDomains from './siteDomains'

export default {
  components: {
    BAlert, BButton, BForm, BFormGroup, BFormInput, BModal, BSpinner,
  },
  props: {
    widgetId: { type: String, default: '' },
    value: { type: String, default: '' },
    hasAdUnitPath: { type: Boolean, default: false },
  },
  data() {
    return {
      visible: false,
      filter: '',
      domains: [],
      loading: false,
      searched: false,
      error: '',
      requestId: 0,
      authUserData: getUserData(),
    }
  },
  computed: {
    isDashboardAdmin() { return isDashboardAdmin(this.authUserData) },
    canSearch() { return this.isDashboardAdmin && Boolean(this.widgetId) && this.hasAdUnitPath },
  },
  watch: {
    widgetId() { this.visible = false; this.invalidate() },
    canSearch(value) {
      if (!value) { this.visible = false; this.invalidate() }
    },
  },
  beforeDestroy() { this.invalidate() },
  methods: {
    invalidate() {
      this.requestId += 1
      this.loading = false
      this.domains = []
      this.searched = false
      this.error = ''
    },
    open() {
      if (!this.canSearch) return
      this.invalidate()
      this.filter = this.value
      this.visible = true
    },
    async search() {
      if (!this.canSearch || !this.visible || this.loading) return
      this.requestId += 1
      const { requestId } = this
      this.loading = true
      this.error = ''
      this.domains = []
      this.searched = false
      try {
        const response = await useJwt.getGoogleWidgetSiteDomains(this.widgetId, this.filter)
        if (requestId !== this.requestId) return
        this.domains = parseSiteDomains(response.data)
        this.searched = true
      } catch (error) {
        if (requestId !== this.requestId) return
        this.error = this.$t('site_domain_picker.error')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    select(domain) {
      if (!this.canSearch || !this.visible || this.loading || !this.domains.includes(domain)) return
      this.$emit('input', domain)
      this.visible = false
    },
  },
}
</script>
